import type * as ecModels from '../../../models'
import * as plugin from '../../plugin'

import { getActionsConfig } from './actions'
import { Config } from './config'
import * as effects from './effects'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * Responsible for all entity data operations, such as CRUD, count, list, etc.
 *
 * This plugin will intelligently figure out how to best execute the operations,
 * using the API, cache, local business logic, etc.
 *
 * The goal is for consumers of this plugin to have a lot of flexibility in
 * their requests, APIs to have a lot of flexibility in their implementations,
 * and this plugin will figure out how to seemlessly and intelligently support
 * all of that flexibility
 */
export class Plugin<
  TTypeParams extends ecModels.TypeParams.Constraint,
> extends plugin.Plugin<
  TTypeParams,
  plugin.Plugin.Id.Standard.DataOperation,
  State<TTypeParams>,
  Selectors<TTypeParams>,
  ReturnType<typeof getActionsConfig<TTypeParams>>
> {
  /**
   * @see {@link Config}
   */
  public readonly config: Readonly<Config<TTypeParams>>

  constructor({ collection, config }: Plugin.constructor.Params<TTypeParams>) {
    super({
      actions: getActionsConfig<TTypeParams>(collection),
      collection,
      id: plugin.Plugin.Id.Standard.DataOperation,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })
    this.config = Config.from(config)
    this.feature.registerEffects(
      effects.executeCount(collection),
      effects.executeGet(collection),
      effects.executeList(collection),
      effects.executeCreate(collection),
      effects.executeUpdate(collection),
      effects.executeDelete(collection),
      effects.cacheGet(this),
      effects.cacheList(this),
      effects.cacheCreate(this),
      effects.cacheUpdate(this),
      effects.cacheDelete(this),
      effects.subscribeChange(collection),
      effects.subscribeDelete(collection),
    )
  }
}
export namespace Plugin {
  export namespace constructor {
    export type Params<TTypeParams extends ecModels.TypeParams.Constraint> =
      Pick<plugin.Plugin.Default<TTypeParams>, 'collection'> & {
        config?: Config.Options<TTypeParams>
      }
  }
}
